import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../lib/api";
import { formatCHF } from "../lib/format";

type Granularity = "month" | "year";

type RevenueRow = {
  period: string;
  einnahmen_rappen: number;
  verrechnet_rappen: number;
};

type RevenueResponse = {
  granularity: Granularity;
  data: RevenueRow[];
};

const LIMITS: Record<Granularity, number> = {
  month: 24,
  year: 10,
};

const GRAN_FILTERS: { key: Granularity; label: string }[] = [
  { key: "month", label: "Monat" },
  { key: "year", label: "Jahr" },
];

export function ReportsPage({ tenantId }: { tenantId: string }) {
  const [granularity, setGranularity] = useState<Granularity>("month");
  const [rows, setRows] = useState<RevenueRow[] | null>(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const mobileFilterRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      if (mobileFilterRef.current && !mobileFilterRef.current.contains(e.target as Node)) {
        setMobileFilterOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  useEffect(() => {
    setRows(null);
    api<RevenueResponse>(
      `/tenants/${tenantId}/reports/revenue?granularity=${granularity}&limit=${LIMITS[granularity]}`,
    ).then((res) => setRows(res.data));
  }, [tenantId, granularity]);

  // API returns newest first; the chart wants oldest → newest.
  const chartData = useMemo(
    () =>
      (rows ?? [])
        .slice()
        .reverse()
        .map((r) => ({
          label: formatPeriod(r.period, granularity),
          einnahmen: r.einnahmen_rappen / 100,
          verrechnet: r.verrechnet_rappen / 100,
        })),
    [rows, granularity],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold tracking-tight">Finanzen</h2>
        <div className="hidden md:flex gap-2 text-sm">
          {GRAN_FILTERS.map((g) => (
            <button
              key={g.key}
              onClick={() => setGranularity(g.key)}
              className={
                "rounded px-2 py-1 " +
                (granularity === g.key
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:text-slate-900")
              }
            >
              {g.label}
            </button>
          ))}
        </div>
        <div className="relative md:hidden" ref={mobileFilterRef}>
          <button
            type="button"
            onClick={() => setMobileFilterOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
          >
            {GRAN_FILTERS.find((g) => g.key === granularity)?.label ?? "Monat"}
            <span className="text-slate-400">▾</span>
          </button>
          {mobileFilterOpen && (
            <div className="absolute right-0 z-10 mt-2 min-w-44 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
              {GRAN_FILTERS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => {
                    setGranularity(g.key);
                    setMobileFilterOpen(false);
                  }}
                  className={
                    "block w-full rounded-lg px-3 py-2 text-left text-sm " +
                    (granularity === g.key
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-50")
                  }
                >
                  {g.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="text-sm text-slate-500 -mt-4">
        Einnahmen = erfasste Zahlungen. Verrechnet = ausgestellte Rechnungen (Stornos negativ).
      </p>

      {rows === null ? (
        <p className="text-slate-400">Lädt…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
          Noch keine Rechnungen oder Zahlungen.
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 p-5">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    tickLine={false}
                    axisLine={{ stroke: "#e2e8f0" }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    tickLine={false}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickFormatter={(v: number) =>
                      new Intl.NumberFormat("de-CH", {
                        notation: "compact",
                        maximumFractionDigits: 1,
                      }).format(v)
                    }
                    width={48}
                  />
                  <Tooltip
                    cursor={{ fill: "#f1f5f9" }}
                    contentStyle={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(value, name) => [
                      formatCHF(Math.round(Number(value ?? 0) * 100)),
                      String(name),
                    ]}
                    labelStyle={{ color: "#0f172a", fontWeight: 500 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="einnahmen" name="Einnahmen" fill="#0f172a" radius={[4, 4, 0, 0]} />
                  <Bar
                    dataKey="verrechnet"
                    name="Verrechnet"
                    fill="#94a3b8"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Periode</th>
                  <th className="text-right font-medium px-4 py-2.5">Einnahmen</th>
                  <th className="text-right font-medium px-4 py-2.5">Verrechnet</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.period} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-medium">
                      {formatPeriod(r.period, granularity)}
                    </td>
                    <td className="px-4 py-2.5 text-right">{formatCHF(r.einnahmen_rappen)}</td>
                    <td
                      className={
                        "px-4 py-2.5 text-right " +
                        (r.verrechnet_rappen < 0 ? "text-red-600" : "text-slate-600")
                      }
                    >
                      {formatCHF(r.verrechnet_rappen)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const MONTHS_DE_SHORT = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
];

function formatPeriod(period: string, g: Granularity): string {
  if (g === "year") return period;
  const [y, m] = period.split("-");
  const idx = parseInt(m ?? "1", 10) - 1;
  return `${MONTHS_DE_SHORT[idx] ?? m} ${y?.slice(2)}`;
}
